import { useState, type Dispatch } from "react";
import { RotateCcw } from "lucide-react-native";
import { Text, View, type ImageSourcePropType } from "react-native";
import {
  ActionBanner,
  ClinicalDataStatus,
  Dialog,
  DialogActions,
  DialogBodyText,
  DialogHeader,
  PrimaryButton,
  ListRowSkeleton,
  MoonToggle,
  Row,
  SecondaryButton,
  semanticColors,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  isNotificationPreferenceEnabled,
  useNotificationDeliverability,
  type NotificationDeliverabilityStatus,
  type NotificationPermissionState,
  type NotificationPreferencesData,
  type SupportedNotificationPreferenceField,
} from "@src/packages/notifications";
import { EMBER } from "../../../../onboarding/theme";
import {
  ReminderTimingEditor,
  type ReminderTimingValue,
} from "@src/packages/ui/ReminderTimingEditor/ReminderTimingEditor";
import {
  GroupedCard,
  YouIllustration,
  YOU_ILLUSTRATION_SIZE,
} from "../components";
import { ICONS } from "../icons";
import type { YouScreenAction } from "../types";

type NotificationPreferencesStatus = {
  isLoading: boolean;
  hasLoadError: boolean;
  hasSaveError: boolean;
  retryLoad: () => void;
  isRetryingLoad: boolean;
  retrySave: () => void;
};

type NotificationRowConfig = {
  id: string;
  field: SupportedNotificationPreferenceField;
  iconSource: ImageSourcePropType;
  labelKey: string;
  descriptionKey: string;
  timingField?: "medicationTimings" | "appointmentTimings";
  timingContext?: "medications" | "appointments";
  timingDefaults?: ReminderTimingValue[];
};

// These are the categories the deployed producer currently delivers. The
// backend stores the same preferences that this dialog exposes; future
// categories stay out of the UI until they can actually notify a patient.
const NOTIFICATION_ROWS: NotificationRowConfig[] = [
  {
    id: "medication-reminders",
    field: "medicationsEnabled",
    iconSource: ICONS.notificationMedication,
    labelKey: "you.reminders_notifications.medications_label",
    descriptionKey: "you.reminders_notifications.medications_description",
    timingField: "medicationTimings",
    timingContext: "medications",
    timingDefaults: ["exact"],
  },
  {
    id: "appointments",
    field: "appointmentsEnabled",
    iconSource: ICONS.notificationAppointment,
    labelKey: "you.reminders_notifications.appointments_label",
    descriptionKey: "you.reminders_notifications.appointments_description",
    timingField: "appointmentTimings",
    timingContext: "appointments",
    timingDefaults: ["1h_before"],
  },
  {
    id: "cycle-updates",
    field: "cycleUpdates",
    iconSource: ICONS.notificationCycle,
    labelKey: "you.reminders_notifications.cycle_updates_label",
    descriptionKey: "you.reminders_notifications.cycle_updates_description",
  },
  {
    id: "test-results",
    field: "testResults",
    iconSource: ICONS.notificationTestResults,
    labelKey: "you.reminders_notifications.test_results_label",
    descriptionKey: "you.reminders_notifications.test_results_description",
  },
  {
    id: "documents",
    field: "documentsEnabled",
    iconSource: ICONS.notificationDocuments,
    labelKey: "you.reminders_notifications.documents_label",
    descriptionKey: "you.reminders_notifications.documents_description",
  },
  {
    id: "coverage-status",
    field: "coverageStatus",
    iconSource: ICONS.notificationCoverage,
    labelKey: "you.reminders_notifications.coverage_status_label",
    descriptionKey: "you.reminders_notifications.coverage_status_description",
  },
];

const VALID_PRESETS = new Set<string>([
  "exact",
  "15m_before",
  "30m_before",
  "1h_before",
]);
const CUSTOM_TIMING_RE = /^custom_\d+[mhd]$/;

function parseTimings(
  raw: unknown,
  defaults: ReminderTimingValue[],
): ReminderTimingValue[] {
  if (!Array.isArray(raw)) return defaults;
  const timings = raw.filter(
    (value): value is ReminderTimingValue =>
      typeof value === "string" &&
      (VALID_PRESETS.has(value) || CUSTOM_TIMING_RE.test(value)),
  );
  return timings.length > 0 ? timings : defaults;
}

function NotificationToggleRow({
  iconSource,
  label,
  description,
  value,
  divider,
  testID,
  onChange,
}: {
  iconSource: ImageSourcePropType;
  label: string;
  description: string;
  value: boolean;
  divider: boolean;
  testID: string;
  onChange: (next: boolean) => void;
}) {
  const descriptionId = `${testID}-description`;

  return (
    <Row
      surface="plain"
      leading={<YouIllustration source={iconSource} />}
      leadingSize={YOU_ILLUSTRATION_SIZE}
      title={label}
      subtitle={
        <Text
          nativeID={descriptionId}
          className="font-dm-sans"
          style={{
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
          }}
        >
          {description}
        </Text>
      }
      trailing={
        <MoonToggle
          value={value}
          onChange={onChange}
          accessibilityLabel={label}
          accessibilityHint={description}
          accessibilityDescriptionId={descriptionId}
          testID={testID}
        />
      }
      chevron={false}
      gap={14}
      paddingHorizontal={16}
      paddingVertical={14}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "500",
        color: EMBER.colors.text,
      }}
      style={{
        borderRadius: 0,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: semanticColors.border.strong,
      }}
    />
  );
}

function NotificationPreferencesSkeleton() {
  return (
    <View testID="notification-preferences-loading-state">
      <GroupedCard>
        {[0, 1, 2, 3].map((index) => (
          <View
            key={index}
            style={{
              borderTopWidth: index > 0 ? 1 : 0,
              borderTopColor: semanticColors.border.strong,
            }}
          >
            <ListRowSkeleton showLeadingSquare showSubtitle showTrailing />
          </View>
        ))}
      </GroupedCard>
    </View>
  );
}

function getDeliverabilityMessageKey(
  status: NotificationDeliverabilityStatus,
  permissionState: NotificationPermissionState,
): string {
  if (status === "token_registered") {
    if (permissionState === "provisional") {
      return "you.reminders_notifications.device_access_limited";
    }
    if (permissionState === "limited") {
      return "you.reminders_notifications.device_access_temporary";
    }
    return "you.reminders_notifications.device_ready";
  }

  switch (status) {
    case "not_requested":
      return "you.reminders_notifications.device_access_not_requested";
    case "token_registration_pending":
      return "you.reminders_notifications.device_connecting";
    case "denied":
      return "you.reminders_notifications.device_access_denied";
    case "restricted":
      return "you.reminders_notifications.device_access_restricted";
    case "unsupported":
      return "you.reminders_notifications.device_unsupported";
    case "token_rejected":
      return "you.reminders_notifications.device_token_rejected";
    case "backend_registration_failed":
      return "you.reminders_notifications.device_registration_failed";
  }
}

function DeliverabilityCard({
  onOpenPermissionEducation,
}: {
  onOpenPermissionEducation: () => void;
}) {
  const t = useTranslations();
  const {
    status,
    permissionState,
    isLoading,
    retryRegistration,
    openDeviceSettings,
  } = useNotificationDeliverability();

  const canOpenSettings =
    status === "denied" ||
    status === "restricted" ||
    (status === "token_registered" && permissionState === "limited");
  const canRetry =
    status === "token_rejected" || status === "backend_registration_failed";
  const isReady =
    status === "token_registered" && permissionState === "authorized";
  const variant: "info" | "warning" | "encouragement" | "important" = isReady
    ? "encouragement"
    : permissionState === "provisional" || permissionState === "limited"
      ? "warning"
      : status === "unsupported" || status === "token_registration_pending"
        ? "info"
        : "important";

  return (
    <View
      testID="notification-deliverability-card"
      style={{ marginBottom: 16 }}
    >
      <ActionBanner
        variant={variant}
        message={t(getDeliverabilityMessageKey(status, permissionState))}
        actionLabel={
          status === "not_requested"
            ? t("you.reminders_notifications.learn_about_device_notifications")
            : canOpenSettings
              ? t("you.reminders_notifications.open_device_settings")
              : canRetry
                ? t("you.reminders_notifications.retry_device_registration")
                : undefined
        }
        actionIcon={
          status !== "not_requested" && !canOpenSettings && canRetry
            ? RotateCcw
            : undefined
        }
        onAction={
          status === "not_requested"
            ? onOpenPermissionEducation
            : canOpenSettings
              ? () => void openDeviceSettings()
              : canRetry
                ? retryRegistration
                : undefined
        }
      />
      {isLoading ? (
        <Text
          className="font-dm-sans"
          style={{
            marginTop: 6,
            paddingHorizontal: 4,
            fontSize: 12,
            lineHeight: 17,
            color: EMBER.colors.textFaint,
          }}
        >
          {t("you.reminders_notifications.device_status_updating")}
        </Text>
      ) : null}
    </View>
  );
}

function NotificationPermissionEducationDialog({
  visible,
  onClose,
  onContinue,
}: {
  visible: boolean;
  onClose: () => void;
  onContinue: () => void;
}) {
  const t = useTranslations();

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout="bottom"
      closeButton="minimal"
      testID="notification-permission-education"
    >
      <View accessibilityRole="header">
        <DialogHeader
          title={t("you.reminders_notifications.permission_education_title")}
        />
      </View>
      <DialogBodyText style={{ marginBottom: 16 }}>
        {t("you.reminders_notifications.permission_education_body")}
      </DialogBodyText>
      <ActionBanner
        variant="info"
        title={t("you.reminders_notifications.permission_education_categories")}
        message={t("you.reminders_notifications.permission_education_privacy")}
        style={{ marginBottom: 16 }}
      />
      <DialogActions>
        <SecondaryButton
          label={t("you.reminders_notifications.permission_education_not_now")}
          onPress={onClose}
          flex={1}
        />
        <PrimaryButton
          label={t("you.reminders_notifications.permission_education_continue")}
          onPress={onContinue}
          flex={1.35}
        />
      </DialogActions>
    </Dialog>
  );
}

type Props = {
  notificationPreferences: NotificationPreferencesData | null | undefined;
  notificationPreferencesStatus: NotificationPreferencesStatus;
  dispatch: Dispatch<YouScreenAction>;
};

/**
 * Notification settings are deliberately a single ordered list rather than
 * several faux sections. The containing detail dialog already gives this
 * content its title; each control maps directly to a persisted API preference.
 */
export default function RemindersNotifications({
  notificationPreferences,
  notificationPreferencesStatus,
  dispatch,
}: Props) {
  const t = useTranslations();
  const [permissionEducationVisible, setPermissionEducationVisible] =
    useState(false);
  const [pendingEnableId, setPendingEnableId] = useState<string | null>(null);
  const {
    status,
    permissionState,
    isLoading: isDeliverabilityLoading,
    requestPermission,
    retryRegistration,
    openDeviceSettings,
  } = useNotificationDeliverability();

  const handleNotificationToggle = (id: string, enabled: boolean) => {
    if (enabled && permissionState === "not_requested") {
      setPendingEnableId(id);
      setPermissionEducationVisible(true);
      return;
    }

    if (enabled && !isDeliverabilityLoading) {
      if (permissionState === "denied" || permissionState === "restricted") {
        void openDeviceSettings();
      } else if (
        status === "token_rejected" ||
        status === "backend_registration_failed"
      ) {
        retryRegistration();
      }
    }
    dispatch({ type: "TOGGLE_NOTIFICATION", id });
  };

  const handlePermissionEducationContinue = () => {
    const id = pendingEnableId;
    setPendingEnableId(null);
    setPermissionEducationVisible(false);

    // The preference remains independent from device deliverability. Save the
    // patient's choice even when the OS prompt is denied or token registration
    // later fails, then show recovery through the status card above.
    if (id) dispatch({ type: "TOGGLE_NOTIFICATION", id });
    void requestPermission();
  };

  const handlePermissionEducationClose = () => {
    setPendingEnableId(null);
    setPermissionEducationVisible(false);
  };

  if (
    notificationPreferencesStatus.isLoading ||
    (!notificationPreferences && !notificationPreferencesStatus.hasLoadError)
  ) {
    return <NotificationPreferencesSkeleton />;
  }

  // The full failure only when nothing loaded; a failed refresh keeps her
  // settings on screen with a stale row.
  if (!notificationPreferences) {
    return (
      <ClinicalDataStatus
        state="unavailable"
        title={t("you.reminders_notifications.load_failed")}
        message={t("states.check_connection_and_try_again")}
        retryLabel={t("clinical_data.retry")}
        retryingLabel={t("clinical_data.retrying")}
        onRetry={notificationPreferencesStatus.retryLoad}
        isRetrying={notificationPreferencesStatus.isRetryingLoad}
        testID="notification-preferences-load-error"
      />
    );
  }

  return (
    <View>
      {notificationPreferencesStatus.hasLoadError ? (
        <ClinicalDataStatus
          state="stale"
          message={t("clinical_data.stale_body")}
          retryLabel={t("clinical_data.retry")}
          retryingLabel={t("clinical_data.retrying")}
          onRetry={notificationPreferencesStatus.retryLoad}
          isRetrying={notificationPreferencesStatus.isRetryingLoad}
          style={{ marginBottom: 12 }}
          testID="notification-preferences-stale"
        />
      ) : null}
      <DeliverabilityCard
        onOpenPermissionEducation={() => setPermissionEducationVisible(true)}
      />
      <GroupedCard>
        {NOTIFICATION_ROWS.map((row, index) => {
          const enabled = isNotificationPreferenceEnabled(
            notificationPreferences,
            row.field,
          );
          const timingConfig =
            row.timingField !== undefined &&
            row.timingContext !== undefined &&
            row.timingDefaults !== undefined
              ? {
                  field: row.timingField,
                  context: row.timingContext,
                  defaults: row.timingDefaults,
                }
              : null;

          return (
            <View key={row.id}>
              <NotificationToggleRow
                iconSource={row.iconSource}
                label={t(row.labelKey)}
                description={t(row.descriptionKey)}
                value={enabled}
                divider={index > 0}
                testID={`notification-preference-${row.id}-toggle`}
                onChange={(next) => handleNotificationToggle(row.id, next)}
              />
              {enabled && timingConfig ? (
                <View
                  style={{
                    paddingTop: 2,
                    paddingRight: 16,
                    paddingBottom: 16,
                    paddingLeft: 58,
                  }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      marginBottom: 8,
                      fontSize: 12,
                      lineHeight: 16,
                      color: EMBER.colors.textFaint,
                    }}
                  >
                    {t("you.reminders_notifications.timing_label")}
                  </Text>
                  <ReminderTimingEditor
                    timings={parseTimings(
                      notificationPreferences[timingConfig.field],
                      timingConfig.defaults,
                    )}
                    onTimingsChange={(timings) =>
                      dispatch({
                        type: "SET_REMINDER_TIMINGS",
                        context: timingConfig.context,
                        timings,
                      })
                    }
                    allowCustomTiming={false}
                    allowEmptySelection
                    theme="light"
                  />
                </View>
              ) : null}
            </View>
          );
        })}
      </GroupedCard>

      {notificationPreferencesStatus.hasSaveError ? (
        <ActionBanner
          variant="important"
          message={t("you.reminders_notifications.save_failed")}
          actionLabel={t("you.reminders_notifications.try_again")}
          actionIcon={RotateCcw}
          onAction={notificationPreferencesStatus.retrySave}
        />
      ) : null}
      <NotificationPermissionEducationDialog
        visible={permissionEducationVisible}
        onClose={handlePermissionEducationClose}
        onContinue={handlePermissionEducationContinue}
      />
    </View>
  );
}
